// composables/useSchoolPrograms.ts
// Composable pour fetcher et gérer les programmes d'une école

import type { ComputedRef, MaybeRefOrGetter } from 'vue'
import { computed, ref, toValue } from 'vue'
import { getAxiosResponseStatus } from '~/utils/axios'
import { parseBlock } from '~/utils/helpers'
import type {
  ProgramLocationsList,
  SchoolPrograms,
  SchoolProgramsResponse,
  SeoSiblingsBlock,
} from '~/types/school'
import { EMPTY_PROGRAMS_LOCATIONS_LIST, EMPTY_SEO_BLOCK } from '~/constants/empty'

interface UseSchoolProgramsOptions {
  language: MaybeRefOrGetter<string>
  alias: MaybeRefOrGetter<string>
}

/**
 * Fetch et gestion des programmes d'une école (page `/ecoles/{alias}/programmes`).
 * @param {UseSchoolProgramsOptions} options Langue + alias route (`école/programmes`).
 * @returns {Promise<{ schoolPrograms: Ref<SchoolProgramsResponse | null>; loading: ComputedRef<boolean>; error: ComputedRef<string | null>; lastHttpStatus: ComputedRef<number | undefined>; refresh: () => Promise<void> }>} Données et état de chargement (SSR via `useAsyncData`).
 */
export const useSchoolPrograms = async (options: UseSchoolProgramsOptions) => {
  const api = useFrontApi()
  const lastHttpStatus = ref<number | undefined>(undefined)

  const {
    data: schoolPrograms,
    error,
    status,
    refresh,
  } = await useAsyncData<SchoolProgramsResponse | null>(
    () => `school-programs-${toValue(options.language)}-${toValue(options.alias)}`,
    async () => {
      const language = toValue(options.language)
      const alias = toValue(options.alias)

      if (!alias || !language) {
        lastHttpStatus.value = undefined
        return null
      }

      const segments = alias.split('/')
      if (segments.length > 3) {
        console.warn(`Alias inattendu : ${alias}. Fetch annulé.`)
        return null
      }
      if (segments.length !== 2) return null

      const schoolAlias = segments[0]
      if (!schoolAlias) {
        console.warn(`Alias de l'école manquant dans ${alias}. Fetch annulé.`)
        return null
      }

      try {
        const endpoint: string = `/api/aca/schoolprograms?language=${language}&alias=${schoolAlias}`
        const { data } = await api.get<SchoolPrograms>(endpoint)

        lastHttpStatus.value = 200

        return {
          schoolprograms: data,
          parsedSchoolTypeBlock: parseBlock<SeoSiblingsBlock>(
            data.schoolTypeBlock,
            EMPTY_SEO_BLOCK,
          ),
          parsedParentBlock: parseBlock<SeoSiblingsBlock>(data.parentBlock, EMPTY_SEO_BLOCK),
          parsedCountriesBlock: parseBlock<SeoSiblingsBlock>(data.countriesBlock, EMPTY_SEO_BLOCK),
          parsedAddressesBlock: parseBlock<ProgramLocationsList>(
            data.addressesBlock,
            EMPTY_PROGRAMS_LOCATIONS_LIST,
          ),
        }
      } catch (e) {
        lastHttpStatus.value = getAxiosResponseStatus(e)
        return null
      }
    },
    {
      watch: [() => toValue(options.language), () => toValue(options.alias)],
    },
  )

  const loading: ComputedRef<boolean> = computed<boolean>(() => status.value === 'pending')

  return {
    schoolPrograms,
    loading,
    error: computed(() => (error.value ? (error.value.message ?? 'Erreur inconnue') : null)),
    lastHttpStatus: computed(() => lastHttpStatus.value),
    refresh,
  }
}
